import { CarFront } from 'lucide-react';
import { carsOf, type CarModel, type CarsContent } from '@london-life/contracts';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Switch } from '@london-life/ui/components/switch';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { Field, NumberField, PlaceSelect, idFor, type NumberSpec } from './form';

type CarsNumbers = Exclude<keyof CarsContent, 'visible' | 'models' | 'showroomPlaceId'>;
const RULES: NumberSpec<CarsNumbers>[] = [
  { key: 'garageMax', label: 'Cars a player can keep', unit: 'number', min: 1, max: 50, whole: true },
  { key: 'resaleShare', label: 'Sells back for this share of the price (0 to 1)', unit: 'number', min: 0, max: 1 },
];
const DRIVE: NumberSpec<CarsNumbers>[] = [
  {
    key: 'driveSeconds',
    label: 'A trip across the map takes (real seconds)',
    unit: 'number',
    min: 3,
    max: 600,
    whole: true,
  },
  { key: 'driveFun', label: 'Fun from a drive', unit: 'number', min: -20, max: 20, whole: true },
  { key: 'flexFun', label: 'Extra fun from a head-turner', unit: 'number', min: 0, max: 30, whole: true },
  {
    key: 'driverSpeed',
    label: 'With a driver, the trip takes this share of the time',
    unit: 'number',
    min: 0.2,
    max: 1,
  },
];
const WEAR: NumberSpec<CarsNumbers>[] = [
  { key: 'wearWeekly', label: 'Condition lost a week', unit: 'number', min: 0, max: 50 },
  { key: 'wearTrip', label: 'Condition lost a trip', unit: 'number', min: 0, max: 20 },
  { key: 'tiredBelow', label: 'Tired (slow to start) below', unit: 'number', min: 0, max: 100, whole: true },
  {
    key: 'serviceShare',
    label: 'A service costs up to this share of the price (0 to 0.2)',
    unit: 'number',
    min: 0,
    max: 0.2,
  },
  { key: 'serviceMinPence', label: 'Smallest service bill', unit: 'pounds', min: 0, max: 1_000_000 },
];
const PRICE: NumberSpec<'pricePence'> = {
  key: 'pricePence',
  label: 'Price',
  unit: 'pounds',
  min: 1,
  max: 100_000_000_000,
};
const FUEL: NumberSpec<'fuelPence'> = {
  key: 'fuelPence',
  label: 'Fuel a trip',
  unit: 'pounds',
  min: 0,
  max: 1_000_000,
};

// Cars (as Lagos Life's Cars): the showroom's models, their prices and fuel, and the rules of the road. Part of
// the content draft, published with it. The showroom place is set here by id (a place on the Places page).
export function CarsPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const cars = carsOf(working ?? {});
  const change = (fn: (draft: CarsContent) => void) =>
    editor.edit((content) => {
      content.cars ??= structuredClone(carsOf(content));
      fn(content.cars);
    });
  const model = (id: string, fn: (entry: CarModel) => void) =>
    change((draft) => {
      const entry = draft.models.find((item) => item.id === id);
      if (entry) fn(entry);
    });
  const numbers = (specs: NumberSpec<CarsNumbers>[]) =>
    specs.map((spec) => (
      <NumberField
        key={spec.key}
        id={`cars-${spec.key}`}
        spec={spec}
        value={cars[spec.key]}
        onChange={(value) => change((draft) => void (draft[spec.key] = value))}
      />
    ));
  const places = working?.places ?? [];
  return (
    <section>
      <PageHeader
        title="Cars"
        description="The showroom (as Lagos Life's Cars): the models on the floor, their prices and fuel, and what driving, wear and a service are like. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="The showroom">
            <h2 className="content-subtitle">
              <CarFront size={16} aria-hidden="true" /> The showroom
            </h2>
            <label className="designer-check">
              <Switch
                checked={cars.visible}
                aria-label="Cars open"
                onCheckedChange={(on) => change((draft) => void (draft.visible = on))}
              />{' '}
              Players can buy cars
            </label>
            <div className="content-grid">
              <PlaceSelect
                id="cars-showroom"
                label="Showroom (a place on the map)"
                value={cars.showroomPlaceId}
                places={places}
                onChange={(placeId) => change((draft) => void (draft.showroomPlaceId = placeId))}
              />
              {numbers(RULES)}
            </div>
          </fieldset>
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Driving">
            <h2 className="content-subtitle">Driving</h2>
            <div className="content-grid">{numbers(DRIVE)}</div>
          </fieldset>
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Wear and service">
            <h2 className="content-subtitle">Wear and service</h2>
            <div className="content-grid">{numbers(WEAR)}</div>
          </fieldset>
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Models">
            <h2 className="content-subtitle">On the floor</h2>
            {cars.models.map((item) => (
              <div className="content-grid content-row" key={item.id} data-testid={`car-${item.id}`}>
                <Field id={`car-${item.id}-label`} label="Model">
                  <Input
                    id={`car-${item.id}-label`}
                    value={item.label}
                    maxLength={40}
                    onChange={(e) => model(item.id, (entry) => void (entry.label = e.target.value))}
                  />
                </Field>
                <Field id={`car-${item.id}-nick`} label="What Londoners call it">
                  <Input
                    id={`car-${item.id}-nick`}
                    value={item.nick}
                    maxLength={30}
                    onChange={(e) => model(item.id, (entry) => void (entry.nick = e.target.value))}
                  />
                </Field>
                <Field id={`car-${item.id}-emoji`} label="Emoji">
                  <Input
                    id={`car-${item.id}-emoji`}
                    value={item.emoji}
                    maxLength={16}
                    onChange={(e) => model(item.id, (entry) => void (entry.emoji = e.target.value))}
                  />
                </Field>
                <Field id={`car-${item.id}-colour`} label="Paint">
                  <Input
                    id={`car-${item.id}-colour`}
                    type="color"
                    value={item.colour}
                    onChange={(e) => model(item.id, (entry) => void (entry.colour = e.target.value))}
                  />
                </Field>
                <NumberField
                  id={`car-${item.id}-price`}
                  spec={PRICE}
                  value={item.pricePence}
                  onChange={(value) => model(item.id, (entry) => void (entry.pricePence = value))}
                />
                <NumberField
                  id={`car-${item.id}-fuel`}
                  spec={FUEL}
                  value={item.fuelPence}
                  onChange={(value) => model(item.id, (entry) => void (entry.fuelPence = value))}
                />
                <Field id={`car-${item.id}-blurb`} label="In a line">
                  <Input
                    id={`car-${item.id}-blurb`}
                    value={item.blurb}
                    maxLength={200}
                    onChange={(e) => model(item.id, (entry) => void (entry.blurb = e.target.value))}
                  />
                </Field>
                <label className="designer-check">
                  <Switch
                    checked={item.bike}
                    aria-label={`${item.label} is a bike`}
                    onCheckedChange={(on) => model(item.id, (entry) => void (entry.bike = on))}
                  />{' '}
                  A bike or scooter (no driver)
                </label>
                <label className="designer-check">
                  <Switch
                    checked={item.flex}
                    aria-label={`${item.label} turns heads`}
                    onCheckedChange={(on) => model(item.id, (entry) => void (entry.flex = on))}
                  />{' '}
                  A head-turner (extra fun)
                </label>
                <label className="designer-check">
                  <Switch
                    checked={item.visible}
                    aria-label={`${item.label} on the floor`}
                    onCheckedChange={(on) => model(item.id, (entry) => void (entry.visible = on))}
                  />{' '}
                  On the floor (players who have one keep it)
                </label>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                change((draft) =>
                  draft.models.push({
                    id: idFor('New car', new Set(draft.models.map((entry) => entry.id)), 'car', 30),
                    label: 'New car',
                    nick: 'The New One',
                    emoji: '🚗',
                    colour: '#9aa3ad',
                    blurb: 'What it is like, in a line.',
                    pricePence: 1_000_000,
                    fuelPence: 200,
                    bike: false,
                    flex: false,
                    visible: false,
                  }),
                )
              }
            >
              Add a car
            </Button>
          </fieldset>
        </div>
      )}
    </section>
  );
}
